import { useParams, useSearchParams } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useReactToPrint } from "react-to-print";
import axios from "axios";
import { Button } from "@mui/material";
import ReportEventData from "./report/ReportEventData";
import ReportClientData from "./report/ReportClientData";
import ReportHeader from "./report/ReportHeader";
import ReportStatusChangeData from "./report/ReportStatusChangeData";
import ReportSupplyData from "./report/ReportSupplyData";
import ReportNewnessData from "./report/ReportNewnessData";
import ReportEvidenceData from "./report/ReportEvidenceData";
import ErrorMessage from "../../../components/common/ErrorMessage";
import LoadingPanel from "../../../components/common/LoadingPanel";
import localize from "../../../common/localize";
import useGlobalContext from "../../../hooks/useGlobalContext";
import ReportReviewForm from "./report/ReportReviewForm";
import ReportEventReview from "./report/ReportEventReview";
import ReportEventResolutions from "./report/ReportEventResolutions";
import ReportAdditionalFields from "./report/ReportAdditionalFields";
import {
  getFooterInformation,
  getReportSections,
} from "../../../common/EntitiesManagement";
import ReportAdditionalCosts from "./report/ReportAdditionalCosts";
import ReportPaymentToTechnician from "./report/ReportPaymentToTechnician";
import ReportSettlement from "./report/ReportSettlement";
import FormSignatures from "./report/FormSignatures";
import { decryptForUrl, EvidenceTypeEnum } from "../../../common/utils";
import { Print } from "@mui/icons-material";
import ReportGeneralRequests from "./report/ReportGeneralRequests";

export default function Report() {
  const {
    EVENTS_API,
    COMPANY_ENDPOINT,
    GENERAL_REQUESTS_ENDPOINT,
    config,
  } = useGlobalContext();
  const [companyData, setCompanyData] = useState(undefined);
  const [searchParams] = useSearchParams();
  const componentRef = useRef();
  const mountedRef = useRef(true);
  const [eventDetailData, setEventDetailData] = useState();
  const [supplyData, setSupplyData] = useState({});
  const [newnessData, setNewnessData] = useState({});
  const [evidenceData, setEvidenceData] = useState({});
  const [statusChangeData, setStatusChangeData] = useState({});
  const [statisticsData, setStatisticsData] = useState({});
  const [eventReviewData, setEventReviewData] = useState({});
  const [eventResolutionsData, setEventResolutionsData] = useState({});
  const [additionalCosts, setAdditionalCost] = useState({});
  const [paymentsToTechnician, setPaymentsToTechnician] = useState({});
  const [eventSettlements, setEventSettlements] = useState([]);
  const [generalRequestsData, setGeneralRequestsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const { id, original } = useParams();
  const [reportSections, setReportSections] = useState(getReportSections(0));

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetState = (setter) => (value) => {
    if (mountedRef.current) {
      setter(value);
    }
  };

  const getCompanyData = async () => {
    const companyRequestResult = await axios.get(`${COMPANY_ENDPOINT}`, config);

    if (companyRequestResult?.data?.Ok && companyRequestResult?.data?.Data) {
      safeSetState(setCompanyData)(companyRequestResult?.data?.Data);
      return;
    }

    safeSetState(setCompanyData)(undefined);
  };

  const validateOperationResult = (result) => {
    if (!result.ok) {
      setErrorMessage(
        JSON.stringify(result?.data || result?.Data || result?.error),
      );
      setShowErrorModal(true);
    }
  };

  const validateOperationResultRaw = (result, isDelete) => {
    if (!result.Ok) {
      setErrorMessage(JSON.stringify(result?.data || result?.Data));
      setShowErrorModal(true);
    }
  };

  const getEventResolutions = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${EVENTS_API}/${id}/resolution`,
      requestConfig,
    );

    safeSetState(setEventResolutionsData)(data?.data);
  };

  const getAdditionalCosts = async () => {
    try {
      const data = await axios.get(
        `${EVENTS_API}/${id}/additionalCost`,
        config,
      );

      validateOperationResultRaw(data.data);
      safeSetState(setAdditionalCost)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getPaymentsToTechnician = async () => {
    try {
      const data = await axios.get(
        `${EVENTS_API}/${id}/paymentToTechnician`,
        config,
      );

      validateOperationResultRaw(data.data);
      safeSetState(setPaymentsToTechnician)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getEventSettlements = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${id}/settlement`, config);
      const body = data?.data;
      safeSetState(setEventSettlements)(
        Array.isArray(body) ? body : (body?.Data ?? []),
      );
    } catch {
      safeSetState(setEventSettlements)([]);
    }
  };

  const getSupplies = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${id}/supply-usage`, config);
      validateOperationResultRaw(data.data);
      safeSetState(setSupplyData)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getNewnesses = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${id}/newness`, config);
      validateOperationResultRaw(data.data);
      safeSetState(setNewnessData)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getEvidences = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${id}/evidence`, config);
      validateOperationResultRaw(data.data);
      safeSetState(setEvidenceData)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getStatusChanges = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${id}/status-change`, config);
      validateOperationResultRaw(data.data);
      safeSetState(setStatusChangeData)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getStatistics = async () => {
    try {
      const data = await axios.get(`${EVENTS_API}/${id}/statistics`, config);
      validateOperationResultRaw(data.data);
      safeSetState(setStatisticsData)(data.data);
    } catch (error) {
      validateOperationResult(error.response.data);
    }
  };

  const getEventReviews = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(`${EVENTS_API}/${id}/review`, requestConfig);

    safeSetState(setEventReviewData)(data?.data);
  };

  const getGeneralRequests = async (eventId) => {
    try {
      const requestConfig = {
        ...config,
        headers: {
          ...config.headers,
          Filter: JSON.stringify([
            {
              Name: "Event",
              FriendlyName: localize.event,
              FilterValue: { Value: eventId },
              Value: eventId,
            },
          ]),
          Pagination: -1,
        },
      };
      const data = await axios.get(
        `${GENERAL_REQUESTS_ENDPOINT}`,
        requestConfig,
      );
      safeSetState(setGeneralRequestsData)(data?.data?.Data ?? []);
    } catch (error) {
      validateOperationResult(error?.response?.data ?? { ok: false });
    }
  };

  const getEventInformation = async (eventId) => {
    if (!eventId) return;
    setIsLoading(true);
    try {
      const eventsDetailObj = await axios.get(
        `${EVENTS_API}/${eventId}`,
        config,
      );

      safeSetState(setEventDetailData)(eventsDetailObj?.data?.Data);

      await Promise.allSettled([
        getCompanyData(),
        getSupplies(),
        getNewnesses(),
        getEvidences(),
        getStatusChanges(),
        getStatistics(),
        getEventReviews(),
        getEventResolutions(),
        getAdditionalCosts(),
        getPaymentsToTechnician(),
        getGeneralRequests(eventId),
        getEventSettlements(),
      ]);
    } catch (error) {
      validateOperationResult(
        error?.response?.data ?? { ok: false, error: error?.message },
      );
    } finally {
      if (mountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const sectionsParam = searchParams?.get("sections");
    if (sectionsParam) {
      setReportSections(JSON.parse(decryptForUrl(sectionsParam)));
    }
    if (id > 0) {
      getEventInformation(id);
    }
  }, [id, searchParams]);

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: `@media print {
      @page {
        size: 600mm 500mm;
        margin: 0;
      }
      a[href]:after {
        content: none !important;
      }
      .print-row {
        break-inside: avoid;
      }
    }`,
  });

  const isReviewAvailable = (reviewsList) => {
    if (!reportSections?.review || !reviewsList) {
      return false;
    }

    const found = reviewsList.find((review) => !review.Evidence);
    return !!found;
  };

  const getSignatureEvidence = (type) => {
    const evidence = evidenceData?.Data?.filter((x) => x.EvidenceType === type);
    if (evidence && evidence.length > 0) return evidence[0];
  };

  if (isLoading) {
    return <LoadingPanel text={localize.loading} />;
  }

  return (
    <div className="w-full flex items-center justify-center align-center">
      <div className="w-full flex-col" style={{ width: "80%" }}>
        <div
          className="w-full flex items-center justify-center align-center"
          ref={componentRef}
          style={{
            "--report-title-bg": reportSections?.titleBackColor || "#10366e",
            "--report-title-fg": reportSections?.titleForeColor || "#ffffff",
          }}
        >
          {showErrorModal && (
            <ErrorMessage
              message={errorMessage}
              title={localize.error_unexpected}
              showModal={showErrorModal}
              setShowModal={setShowErrorModal}
            />
          )}
          <div className="w-full flex-col items-center justify-center align-center">
            <ReportHeader
              event={eventDetailData}
              isOriginal={original === "true"}
              showContractorData={reportSections?.useContractorHeader}
            />
            {reportSections?.eventInfo && (
              <ReportEventData
                event={eventDetailData}
                statistics={statisticsData}
                additionalCosts={additionalCosts?.Data}
              />
            )}

            {reportSections?.clientInfo && eventDetailData?.Client && (
              <ReportClientData clientData={eventDetailData?.Client} />
            )}

            <FormSignatures
              footerInformation={getFooterInformation(companyData)}
              showSignatures={reportSections?.formSignatures}
              finalObservation={eventDetailData?.FinishObservation}
              eventId={eventDetailData?.Id}
              clientSignature={getSignatureEvidence(5)}
              personalAssignedSignature={getSignatureEvidence(6)}
            />

            {reportSections?.additionalData &&
              eventDetailData?.AdditionalFields?.length > 2 && (
                <ReportAdditionalFields
                  additionalFields={JSON.parse(
                    eventDetailData?.AdditionalFields,
                  )}
                />
              )}

            {reportSections?.statusChanges &&
              statusChangeData?.Data?.length > 0 && (
                <ReportStatusChangeData
                  eventDetailData={eventDetailData}
                  statusChangeStoryTime={statusChangeData}
                  isOriginal={original === "true"}
                  formFormat={reportSections?.form_results_format}
                  hideModificationDate={reportSections?.hideModificationDate}
                />
              )}

            {reportSections?.visitReport &&
              eventDetailData?.EventType?.ReviewForm && (
                <ReportReviewForm
                  dynamicForm={eventDetailData?.EventType?.ReviewForm}
                  eventId={eventDetailData?.Id}
                  original={original === "true"}
                  formResultsFormatting={reportSections?.form_results_format}
                  hideModificationDate={reportSections?.hideModificationDate}
                />
              )}
            {reportSections?.eventResolutions &&
              eventResolutionsData?.Data?.length > 0 && (
                <ReportEventResolutions
                  resolutions={eventResolutionsData?.Data}
                />
              )}

            {reportSections?.generalRequests && 
              generalRequestsData?.length > 0 && (
              <ReportGeneralRequests requests={generalRequestsData} />
            )}

            {reportSections?.supplies && supplyData?.Data?.length > 0 && (
              <ReportSupplyData supplyUsageData={supplyData?.Data} />
            )}
            {reportSections?.newness && newnessData?.Data?.length > 0 && (
              <ReportNewnessData newnessesData={newnessData?.Data} />
            )}
            {isReviewAvailable(eventReviewData?.Data) && (
              <ReportEventReview
                reviews={eventReviewData?.Data}
                eventDetailData={eventDetailData}
              />
            )}
            {reportSections?.additionalCosts &&
              additionalCosts?.Data?.length > 0 && (
                <ReportAdditionalCosts
                  additionalCostData={additionalCosts?.Data}
                />
              )}
            {reportSections?.settlement && eventSettlements?.length > 0 && (
              <ReportSettlement settlementDetails={eventSettlements} />
            )}
            {reportSections?.paymentToTechnician &&
              paymentsToTechnician?.Data?.length > 0 && (
                <ReportPaymentToTechnician
                  paymentsToTechnician={paymentsToTechnician?.Data}
                />
              )}
            {reportSections?.evidences &&
              evidenceData?.Data?.filter(
                (x) =>
                  x.EvidenceType !== EvidenceTypeEnum.ClientSignature &&
                  x.EvidenceType !== EvidenceTypeEnum.PersonalSignature,
              )?.length > 0 && (
                <ReportEvidenceData
                  evidences={evidenceData?.Data}
                  reviews={eventReviewData?.Data}
                  eventId={eventDetailData?.Id}
                  original={original === "true"}
                  isPanelReport={reportSections?.panel_report}
                  columnsForPanelReport={reportSections?.columns ?? 3}
                  hideModificationDate={reportSections?.hideModificationDate}
                  notVisibleEvidences={reportSections?.notVisibleEvidences}
                />
              )}
          </div>
        </div>
        <div className="flex w-full justify-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handlePrint}
            style={{
              marginTop: "20px",
            }}
          >
            {localize.generate_pdf}
            <Print style={{ color: "white", marginLeft: "5px" }} />
          </Button>
        </div>

        <div style={{ marginTop: "20px" }} />
      </div>
    </div>
  );
}
